// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';

import SessionManager from '@app/features/platform/state/AuthSession';
import * as ShopCommands from '@app/features/shop/commands/ShopCommands';
import {
	itemSku,
	type ShopBundleEntry,
	type ShopEntry,
	type ShopItemEntry,
	type ShopPackageEntry,
	type ShopUsernameCardEntry,
	type ShopUsernameProductEntry,
} from '@app/features/shop/utils/ShopEntries';
import {campaignShown, inPayableUnits, unsoldPrice} from '@app/features/shop/utils/ShopPriceFormat';
import type {RecurringEligibility, RecurringQuote} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import type {ShopViewerPayment} from '@fluxer/schema/src/domains/shop/ShopPaymentSchemas';
import type {
	ShopArtResponse,
	ShopBoostResponse,
	ShopPriceResponse,
	ShopSettingsResponse,
	ShopUsernameProductResponse,
	ShopVisionaryResponse,
} from '@fluxer/schema/src/domains/shop/ShopResponseSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

// The catalog is the same for every account apart from the viewer flag, so a session reads it at most every five
// minutes. Card prices are display only: the checkout shows the server's quote and the order is priced again.
const CATALOG_TTL_MS = 5 * 60 * 1000;
// Named usernames sell one by one, so their first page is read again sooner; a purchase reads it at once.
const USERNAME_PRODUCTS_TTL_MS = 60 * 1000;
// setTimeout fires at once above this; a later campaign end is waited for in steps.
const MAX_TIMER_MS = 2_147_483_647;
// An older server names no route: it sells through Payriff, in TL while it has a TL rate.
const PAYRIFF_ROUTE: ShopViewerPayment = {provider: 'payriff', currency: 'TRY'};

class ShopCatalog {
	status: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';
	settings: ShopSettingsResponse | null = null;
	itemEntries: Array<ShopItemEntry> = [];
	packageEntries: Array<ShopPackageEntry> = [];
	bundleEntries: Array<ShopBundleEntry> = [];
	// The bundles come in the admin's order (the first is featured); false: ID order and the ultras-first rule.
	bundlesOrdered = false;
	// The shop front's art from admin; null: the client's own.
	frontArt: ShopArtResponse | null = null;
	usernameCards: Array<ShopUsernameCardEntry> = [];
	// Named usernames on sale, in name order, a page at a time; read only where the shop shows them.
	usernameProducts: Array<ShopUsernameProductEntry> = [];
	usernameProductsStatus: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';
	// A Visionary with an unused free username: the lengths the shop setting allows; empty for everyone else.
	visionaryNameLengths: ReadonlyArray<1 | 2 | 3> = [];
	visionary: ShopVisionaryResponse | null = null;
	// The boost slot on sale; null without a price, for a Paddle buyer without the Paddle boost product, where the
	// server names no payable amount for it, and from an older API.
	boost: ShopBoostResponse | null = null;
	serverTimeOffsetMs = 0;
	premiumDiscount = false;
	// Renewing Plus: whether this viewer may start one and each package's period total; null and empty where it is off.
	recurringEligibility: RecurringEligibility | null = null;
	recurringQuotes = new Map<string, RecurringQuote>();
	// Who sells to this viewer, from the country of the request; null: nobody sells here.
	payment: ShopViewerPayment | null = PAYRIFF_ROUTE;
	private generation = 0;
	private loadedAt = 0;
	private loadedFor: string | null = null;
	private pending: Promise<void> | null = null;
	private usernameProductsAfter: string | null = null;
	private usernameProductsRequest = 0;
	private usernameProductsLoadedAt = 0;
	private usernameProductsPending: Promise<void> | null = null;
	private tryRateExpiryTimer: ReturnType<typeof setTimeout> | null = null;
	// Moves when a shown campaign ends; now() reads it, so everything drawn from the clock is drawn again.
	private campaignClock = 0;
	private campaignTimer: ReturnType<typeof setTimeout> | null = null;

	// A refusal's own eligibility, shown until the reloaded catalog brings the same.
	setRecurringEligibility(eligibility: RecurringEligibility): void {
		this.recurringEligibility = eligibility;
	}

	// Renewing Plus is on sale where this viewer is, even if not to this account right now: a package carries a renewal
	// quote and the eligibility names no reason that closes renewal for everyone. A missing eligibility shows nothing.
	get recurringOffered(): boolean {
		const reason = this.recurringEligibility?.reason;
		return (
			this.recurringQuotes.size > 0 &&
			this.recurringEligibility !== null &&
			reason !== 'sales_closed' &&
			reason !== 'region_unavailable'
		);
	}

	// Micup: Plus sells as a renewing subscription to this account, and every Plus surface says so instead of
	// "one-time"; otherwise (Payriff, renewal closed, or an account that may not start one) Plus is bought once.
	get plusRenews(): boolean {
		return this.recurringOffered && this.recurringEligibility?.eligible === true;
	}

	constructor() {
		makeAutoObservable<
			this,
			'tryRateExpiryTimer' | 'campaignTimer' | 'generation' | 'usernameProductsRequest' | 'usernameProductsPending'
		>(
			this,
			{
				tryRateExpiryTimer: false,
				campaignTimer: false,
				generation: false,
				usernameProductsRequest: false,
				usernameProductsPending: false,
			},
			{autoBind: true},
		);
	}

	// viewer.premium_discount belongs to one account, so a switched account reads the catalog again.
	load(force = false): Promise<void> {
		if (AccountScopedWork.isSuspended) return Promise.resolve();
		this.refreshTryRateExpiry();
		const fresh =
			this.status === 'ready' &&
			this.loadedFor === SessionManager.currentAccountKey &&
			Date.now() - this.loadedAt < CATALOG_TTL_MS;
		if (!this.pending && (force || !fresh)) {
			this.pending = this.fetchCatalog();
		}
		return this.pending ?? Promise.resolve();
	}

	reset(): void {
		this.generation++;
		if (this.tryRateExpiryTimer !== null) clearTimeout(this.tryRateExpiryTimer);
		this.tryRateExpiryTimer = null;
		if (typeof document !== 'undefined')
			document.removeEventListener('visibilitychange', this.onTryRateVisibilityChange);
		this.status = 'idle';
		this.settings = null;
		this.itemEntries = [];
		this.packageEntries = [];
		this.bundleEntries = [];
		this.bundlesOrdered = false;
		this.frontArt = null;
		this.usernameCards = [];
		this.usernameProducts = [];
		this.usernameProductsStatus = 'idle';
		this.usernameProductsAfter = null;
		this.usernameProductsRequest++;
		this.usernameProductsLoadedAt = 0;
		this.usernameProductsPending = null;
		this.visionaryNameLengths = [];
		this.visionary = null;
		this.boost = null;
		this.serverTimeOffsetMs = 0;
		this.premiumDiscount = false;
		this.recurringEligibility = null;
		this.recurringQuotes = new Map();
		this.payment = PAYRIFF_ROUTE;
		this.loadedAt = 0;
		this.loadedFor = null;
		this.pending = null;
	}

	private async fetchCatalog(): Promise<void> {
		const generation = this.generation;
		if (this.status !== 'ready') this.status = 'loading';
		const viewerId = SessionManager.currentAccountKey;
		try {
			const catalog = await ShopCommands.fetchCatalog();
			runInAction(() => {
				if (generation !== this.generation) return;
				const payment = catalog.viewer.payment === undefined ? PAYRIFF_ROUTE : catalog.viewer.payment;
				// The sale switches and the TL rate are shared by every viewer; this viewer's route narrows them: no seller
				// closes both sales, Payriff in AZN shows no TL.
				this.settings = {
					...catalog.settings,
					...(payment === null && {premium_sales_open: false, item_sales_open: false}),
					...(payment?.provider === 'payriff' && payment.currency === 'AZN' && {try_to_azn_rate_micro: null}),
				};
				this.payment = payment;
				this.recurringEligibility = catalog.viewer.recurring_eligibility ?? null;
				this.recurringQuotes = new Map(
					catalog.packages.flatMap(({sku, recurring_quote}) => (recurring_quote ? [[sku, recurring_quote]] : [])),
				);
				const view = <T extends ShopPriceResponse>(price: T): T =>
					inPayableUnits(price, catalog.viewer.premium_discount, this.settings?.try_to_azn_rate_micro);
				this.itemEntries = catalog.items.map(({item, price}) => ({
					kind: 'item',
					sku: itemSku(item.id),
					item,
					price: price && view(price),
				}));
				this.packageEntries = catalog.packages.map(({sku, months, price}) => ({
					kind: 'package',
					sku,
					months,
					price: view(price),
				}));
				this.bundleEntries = (catalog.bundles ?? []).map((bundle) => ({
					kind: 'bundle',
					sku: bundle.sku,
					bundle,
					price: view(bundle.price),
				}));
				this.bundlesOrdered = catalog.bundles_ordered ?? false;
				this.frontArt = catalog.front_art ?? null;
				this.usernameCards = (catalog.username_cards ?? []).map((card) => ({
					kind: 'username',
					...card,
					price: view(card.price),
				}));
				this.visionary = catalog.visionary && {...catalog.visionary, price: view(catalog.visionary.price)};
				this.boost =
					catalog.boost && !unsoldPrice(catalog.boost.price)
						? {...catalog.boost, price: view(catalog.boost.price)}
						: null;
				this.premiumDiscount = catalog.viewer.premium_discount;
				this.visionaryNameLengths = catalog.viewer.visionary_name_lengths ?? [];
				this.serverTimeOffsetMs = Date.parse(catalog.server_time) - Date.now();
				this.refreshTryRateExpiry();
				this.refreshCampaignEnd();
				this.loadedAt = Date.now();
				this.loadedFor = viewerId;
				this.status = 'ready';
			});
		} catch {
			// A failed reload in the background keeps the catalog it had on screen.
			runInAction(() => {
				if (generation !== this.generation) return;
				this.refreshTryRateExpiry();
				if (this.status !== 'ready') this.status = 'failed';
			});
		} finally {
			runInAction(() => {
				if (generation !== this.generation) return;
				this.pending = null;
			});
		}
	}

	// The first page again, at most once a minute unless forced: a name sold meanwhile leaves the list. A fresh list
	// keeps the pages "More" loaded, and a read on its way is shared.
	loadUsernameProducts(force = false): Promise<void> {
		if (this.usernameProductsPending) return this.usernameProductsPending;
		const fresh =
			this.usernameProductsStatus === 'ready' && Date.now() - this.usernameProductsLoadedAt < USERNAME_PRODUCTS_TTL_MS;
		if (fresh && !force) return Promise.resolve();
		const pending = this.fetchUsernameProducts(null).finally(() =>
			runInAction(() => {
				if (this.usernameProductsPending === pending) this.usernameProductsPending = null;
			}),
		);
		this.usernameProductsPending = pending;
		return pending;
	}

	get usernameProductsHaveMore(): boolean {
		return this.usernameProductsAfter !== null;
	}

	loadMoreUsernameProducts(): Promise<void> {
		if (this.usernameProductsAfter === null || this.usernameProductsStatus === 'loading') return Promise.resolve();
		return this.fetchUsernameProducts(this.usernameProductsAfter);
	}

	private async fetchUsernameProducts(after: string | null): Promise<void> {
		if (AccountScopedWork.isSuspended) return;
		const request = ++this.usernameProductsRequest;
		this.usernameProductsStatus = 'loading';
		try {
			const page = await ShopCommands.fetchUsernameProducts(after);
			runInAction(() => {
				if (request !== this.usernameProductsRequest) return;
				const entries = page.items.map(this.usernameProductEntry);
				this.usernameProducts = after === null ? entries : [...this.usernameProducts, ...entries];
				this.usernameProductsAfter = page.next_after;
				if (after === null) this.usernameProductsLoadedAt = Date.now();
				this.usernameProductsStatus = 'ready';
			});
		} catch {
			runInAction(() => {
				if (request === this.usernameProductsRequest) this.usernameProductsStatus = 'failed';
			});
		}
	}

	private usernameProductEntry(product: ShopUsernameProductResponse): ShopUsernameProductEntry {
		return {
			kind: 'username',
			...product,
			// A username price has no member discount, as the name check prices it.
			price: inPayableUnits(product.price, false, this.settings?.try_to_azn_rate_micro),
			asset: null,
			visible: true,
		};
	}

	// One local timer for the catalog, never a fetch or a timer per price. Overrides have no expiry.
	private refreshTryRateExpiry(): void {
		if (this.tryRateExpiryTimer !== null) clearTimeout(this.tryRateExpiryTimer);
		this.tryRateExpiryTimer = null;
		if (typeof document !== 'undefined')
			document.removeEventListener('visibilitychange', this.onTryRateVisibilityChange);
		const settings = this.settings;
		if (settings?.try_to_azn_rate_micro == null || !settings.try_rate_expires_at) return;
		const remaining = Date.parse(settings.try_rate_expires_at) - this.now();
		if (remaining <= 0) {
			settings.try_to_azn_rate_micro = null;
			this.dropLiraPrices();
			return;
		}
		this.tryRateExpiryTimer = setTimeout(this.refreshTryRateExpiry, remaining);
		if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onTryRateVisibilityChange);
	}

	// Without a TL rate the server charges no Payriff lira price: every price falls back to its manat.
	private dropLiraPrices(): void {
		const view = <T extends ShopPriceResponse>(price: T): T => inPayableUnits(price, this.premiumDiscount, null);
		this.itemEntries = this.itemEntries.map((entry) => ({...entry, price: entry.price && view(entry.price)}));
		this.packageEntries = this.packageEntries.map((entry) => ({...entry, price: view(entry.price)}));
		this.bundleEntries = this.bundleEntries.map((entry) => ({...entry, price: view(entry.price)}));
		this.usernameCards = this.usernameCards.map((entry) => ({...entry, price: view(entry.price)}));
		if (this.visionary) this.visionary = {...this.visionary, price: view(this.visionary.price)};
		if (this.boost) this.boost = {...this.boost, price: view(this.boost.price)};
		this.usernameProducts = this.usernameProducts.map((entry) => ({
			...entry,
			price: inPayableUnits(entry.price, false, null),
		}));
	}

	// One timer for the catalog, at the first shown campaign's end among every price it sells.
	private refreshCampaignEnd(): void {
		if (this.campaignTimer !== null) clearTimeout(this.campaignTimer);
		this.campaignTimer = null;
		const now = this.now();
		const prices: Array<ShopPriceResponse | null> = [
			...this.itemEntries.map((entry) => entry.price),
			...this.bundleEntries.map((entry) => entry.price),
			...this.packageEntries.map((entry) => entry.price),
			...this.usernameCards.map((entry) => entry.price),
			this.visionary?.price ?? null,
			this.boost?.price ?? null,
		];
		const ends = prices.flatMap((price) =>
			price?.campaign_ends_at && campaignShown(price, now) ? [Date.parse(price.campaign_ends_at)] : [],
		);
		if (ends.length === 0) return;
		this.campaignTimer = setTimeout(this.onCampaignEnd, Math.min(Math.min(...ends) - now + 1, MAX_TIMER_MS));
	}

	private onCampaignEnd(): void {
		this.campaignClock++;
		this.refreshCampaignEnd();
	}

	private onTryRateVisibilityChange(): void {
		if (document.visibilityState === 'visible') this.refreshTryRateExpiry();
	}

	// Visionary is not a shop entry: it lives in `visionary` and the premium page sells it.
	entryForSku(sku: string): ShopEntry | null {
		return (
			this.itemEntries.find((entry) => entry.sku === sku) ??
			this.packageEntries.find((entry) => entry.sku === sku) ??
			this.bundleEntries.find((entry) => entry.sku === sku) ??
			this.usernameCards.find((entry) => entry.sku === sku) ??
			null
		);
	}

	// Campaign countdowns run on the server's clock.
	now(): number {
		void this.campaignClock;
		return Date.now() + this.serverTimeOffsetMs;
	}

	get premiumIncludedCount(): number {
		return this.itemEntries.filter((entry) => entry.item.availability === 'premium').length;
	}
}

const shopCatalog = new ShopCatalog();
ResettableStates.register(shopCatalog, shopCatalog.reset);
AccountScopedWork.registerCancellation(() => shopCatalog.reset());

export default shopCatalog;
